import { Action, Getter, Mutation, State } from 'vuex-simple';
import { serverProps } from '@/Common/Environment/const';

import HelpCenterAnalytics, { EVENT_TYPE } from '@/HostApp/services/HelpCenterService/HelpCenterAnalytics';
import httpServiceModule from '@/HostApp/services/HttpService/HttpServiceModule';

const { aps } = window;

enum MESSAGE_TYPE {
	REQUEST_ARTICLE = 'requestArticle',
	PROVIDE_FEEDBACK = 'provideFeedback',
	PAGE_VIEW = 'pageView',
	SEARCH = 'search',
	SEARCH_RESULT_CLICKED = 'searchResultClicked',
	LINK_CLICKED = 'linkClicked',
	SET_CONTENT_HEIGHT = 'setContentHeight'
}

enum RESPONSE_TYPES {
	REQUEST_ARTICLE_ERROR = 'requestArticleError',
	REQUEST_ARTICLE_SUCCESS = 'requestArticleSuccess',
	FEEDBACK_ERROR = 'feedbackError',
	FEEDBACK_SUCCESS = 'feedbackSuccess'
}

enum FEEDBACK {
	YES = 'Yes',
	NO = 'No'
}

const ACCOUNT_TYPE: Record<string, number> = {
	UNKNOWN: 0,
	ADMIN: 0,
	PROVIDER: 0,
	RESELLER: 1,
	CLIENT: 2
};

const LANGUAGE_MAPPING: Record<string, string> = {
	['en']: 'en-us',
	['es']: 'es',
	['de']: 'de',
	['fr']: 'fr',
	['it']: 'it',
	['ja']: 'ja',
	['nl']: 'nl',
	['pt']: 'pt-br',
	['tr']: 'tr'
};

export class HelpCenterServiceModule {
	constructor() {
		this.setIsEnabled(serverProps.showContextHelp);
		this.setBaseUrl(serverProps.contextHelpUrl);
	}

	@Getter()
	public get backendBaseUrl(): string {
		return '/aps/2/services/helpcenter-manager';
	}

	@State()
	public helpCenterVueIFrame?: HTMLIFrameElement;

	@Mutation()
	public setHelpCenterVueIFrame(vueIFrame: HTMLIFrameElement): void {
		this.helpCenterVueIFrame = vueIFrame;
	}

	@State()
	public componentName: string = '';

	@Mutation()
	public setComponentName(value: string): void {
		this.componentName = value;
	}

	@State()
	public version: string = '';

	@Mutation()
	public setVersion(value: string): void {
		this.version = value;
	}

	@State()
	public articleId: string = '';

	@Mutation()
	public setArticleId(value: string): void {
		this.articleId = value;
	}

	@State()
	public isLoading: boolean = false;

	@Mutation()
	public setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	@State()
	public baseUrl: string = '';

	@Mutation()
	public setBaseUrl(value: string = ''): void {
		this.baseUrl = value.replace(/\/+$/, '');
	}

	@State()
	public articlePath: string = '';

	@Mutation()
	public setArticlePath(value: string): void {
		this.articlePath = value;
	}

	@State()
	public isEnabled: boolean = false;

	@Mutation()
	public setIsEnabled(value: boolean): void {
		this.isEnabled = value;
	}

	@State()
	public iframeHeight: null | number = null;

	@Mutation()
	public setIframeHeight(value: number): void {
		this.iframeHeight = value;
	}

	@State()
	public isHelpCenterOpened: boolean = false;

	@Mutation()
	public setIsHelpCenterOpened(value: boolean): void {
		if (this.isHelpCenterOpened === value) {
			return;
		}
		const currentTimestamp = Date.now();
		if (value) {
			this.sendInitialPageViewEvent();
			this.refreshCurrentArticle();
			this.initialTimestamp = currentTimestamp;
			this.partialTimestamp = currentTimestamp;
		} else {
			const spentTime = currentTimestamp - this.initialTimestamp;
			const timeLimit = 2000;
			if (spentTime > timeLimit) {
				this.sendClosingEvent(spentTime);
				this.processPageView(this.currentInnerPage);
			}
			this.initialTimestamp = 0;
			this.partialTimestamp = 0;
			this.setArticlePath('about:blank');
		}
		this.isHelpCenterOpened = value;
	}

	@State()
	public articleTitle: string = '';

	@Mutation()
	public setArticleTitle(value: string): void {
		this.articleTitle = value;
	}

	@State()
	public initialTimestamp: number = 0;

	@Mutation()
	public setInitialTimestamp(value: number): void {
		this.initialTimestamp = value;
	}

	@State()
	public partialTimestamp: number = 0;

	@Mutation()
	public setPartialTimestamp(value: number): void {
		this.partialTimestamp = value;
	}

	/**
	 * The URL of the current page the helpCenter iframe is on, as you can navigate through pages inside it.
	 */
	@State()
	public currentInnerPage: string = '';

	@Mutation()
	public setCurrentInnerPage(value: string): void {
		this.currentInnerPage = value;
	}

	@Getter()
	public get currentInnerPageName(): string {
		if (!this.currentInnerPage) {
			return this.articleId;
		}

		const splitted = this.currentInnerPage.split('/');

		return splitted[splitted.length - 1].split('.')[0];
	}

	@Action()
	public async requestArticle(): Promise<void> {
		this.setIsLoading(true);
		const accountType = aps.context.accountType || 'UNKNOWN';
		const url = `${this.backendBaseUrl}/request-new-article`;
		const userType = ACCOUNT_TYPE[accountType] !== undefined ? ACCOUNT_TYPE[accountType] : ACCOUNT_TYPE['UNKNOWN'];

		const parameters = {
			userType,
			componentName: this.componentName,
			componentVersion: this.version,
			articleId: this.articleId,
			locale: aps.context._locale
		};

		const response: { error: string } = (await httpServiceModule.post<{ error: string }>(url, parameters)).data;

		this.sendAnalyticsEvent({
			type: EVENT_TYPE.REQUEST_ARTICLE,
			payload: {
				eventAction: `${this.articleId};${this.currentLanguage}`
			}
		});

		this.postMessageToIframe(
			!response || response.error ? RESPONSE_TYPES.REQUEST_ARTICLE_ERROR : RESPONSE_TYPES.REQUEST_ARTICLE_SUCCESS
		);

		this.setIsLoading(false);
	}

	@Action()
	public async provideFeedback(payload: { useful: string; path: string }): Promise<void> {
		if (!Object.values(FEEDBACK).includes(payload.useful as FEEDBACK)) {
			return;
		}

		const accountType = aps.context.accountType || 'UNKNOWN';
		const url = `${this.backendBaseUrl}/feedback`;
		const explodedPath = payload.path.split('/');
		const userType = ACCOUNT_TYPE[accountType] !== undefined ? ACCOUNT_TYPE[accountType] : ACCOUNT_TYPE['UNKNOWN'];
		const nameIndex = 3;
		const versionIndex = 2;
		const articleId = explodedPath[explodedPath.length - 1].split('.')[0].toLowerCase();

		const parameters = {
			userType,
			articleId,
			useful: payload.useful === FEEDBACK.YES,
			componentName: explodedPath[explodedPath.length - nameIndex],
			componentVersion: explodedPath[explodedPath.length - versionIndex],
			locale: aps.context._locale
		};

		const response: { error: string } = (await httpServiceModule.post<{ error: string }>(url, parameters)).data;

		if (userType !== ACCOUNT_TYPE.PROVIDER) {
			this.sendAnalyticsEvent({
				type: EVENT_TYPE.HELPFUL_ARTICLE,
				payload: {
					eventAction: payload.useful,
					eventLabel: articleId
				},
				overrideUrl: payload.path
			});
		}

		this.postMessageToIframe(
			!response || response.error ? RESPONSE_TYPES.FEEDBACK_ERROR : RESPONSE_TYPES.FEEDBACK_SUCCESS
		);
	}

	private sendAnalyticsEvent(data: {
		type: EVENT_TYPE;
		payload: Partial<Analytics.IGAEvent>;
		overrideUrl?: string;
	}): void {
		const { type, payload, overrideUrl } = data;
		HelpCenterAnalytics.buildAndSendEvent(type, payload, {
			pageUrl: overrideUrl || this.cleanArticlePath,
			userId: this.userId
		});
	}

	private processPageView(currentUrl: string): void {
		if (this.currentInnerPage) {
			const spentTime = Date.now() - this.partialTimestamp;
			this.sendAnalyticsEvent({
				type: EVENT_TYPE.TIME_ON_SCREEN,
				payload: {
					eventAction: this.currentInnerPageName,
					eventValue: spentTime
				},
				overrideUrl: this.currentInnerPage
			});
		}
		this.setCurrentInnerPage(currentUrl);
		this.setPartialTimestamp(Date.now());
	}

	private sendClosingEvent(spentTime: number): void {
		this.sendAnalyticsEvent({
			type: EVENT_TYPE.HELP_CENTER_CLOSED,
			payload: {
				eventValue: spentTime
			}
		});
	}

	private sendInitialPageViewEvent(): void {
		this.sendAnalyticsEvent({
			type: EVENT_TYPE.ARTICLE_VIEWED_FROM_PAGE,
			payload: {
				eventLabel: this.articleTitle ? this.articleId : 'Request article'
			}
		});
	}

	private postMessageToIframe(messageType: RESPONSE_TYPES): void {
		this.helpCenterVueIFrame?.contentWindow?.postMessage([messageType], '*');
	}

	@Action()
	public messageEventListener(event: MessageEvent): void {
		const { id, data } = event.data;

		if (this.baseUrl.indexOf(event.origin) === -1 || !Object.values(MESSAGE_TYPE).includes(id)) {
			return;
		}

		switch (id) {
			case MESSAGE_TYPE.PROVIDE_FEEDBACK:
				this.provideFeedback({ useful: data.useful, path: data.path });
				break;
			case MESSAGE_TYPE.REQUEST_ARTICLE:
				this.requestArticle();
				break;
			case MESSAGE_TYPE.SEARCH:
				this.sendAnalyticsEvent({
					type: EVENT_TYPE.SEARCH,
					payload: { eventAction: data.query },
					overrideUrl: data.currentUrl
				});
				break;
			case MESSAGE_TYPE.SEARCH_RESULT_CLICKED:
				this.sendAnalyticsEvent({
					type: EVENT_TYPE.SEARCH_RESULT_CLICKED,
					payload: { eventLabel: `${data.query};${data.name}` },
					overrideUrl: data.currentUrl
				});
				break;
			case MESSAGE_TYPE.LINK_CLICKED:
				this.sendAnalyticsEvent({
					type: EVENT_TYPE.LINK_CLICKED,
					payload: { eventLabel: data.name },
					overrideUrl: data.currentUrl
				});
				break;
			case MESSAGE_TYPE.PAGE_VIEW:
				this.processPageView(data.currentUrl);
				break;
			case MESSAGE_TYPE.SET_CONTENT_HEIGHT:
				const { height } = data;
				this.setIframeHeight(height);
				break;
		}
	}

	@Action()
	public async extractArticleTitle(): Promise<void> {
		if (this.articlePath.includes('/404.htm')) {
			this.setArticleTitle('');

			return;
		}

		let response;
		try {
			response = await fetch(this.articlePath);
		} catch (e) {
			this.setArticleTitle('');

			return;
		}

		if (response.url.includes('/404.htm')) {
			this.setArticleTitle('');

			return;
		}

		const html = await response.text();
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');

		const mainTitleTag = doc.querySelector('h1');
		const title = mainTitleTag?.textContent || '';

		this.setArticleTitle(title);
	}

	@Action()
	public refreshCurrentArticle(): void {
		const { context = [] } = aps.navigation?.tree || {};
		const viewId = context.length ? context[context.length - 1]?.viewId || '' : '';

		this.computeNewArticlePath(viewId);
		this.setIsLoading(false);
	}

	@Action()
	public computeNewArticlePath(val: string): void {
		this.setIsHelpCenterOpened(false);
		this.setIsLoading(true);

		let formattedUrl = this.getArticleUrl(val);
		formattedUrl += `?origin=${window.location.origin}`;

		this.setArticlePath(formattedUrl);
		this.extractArticleTitle();
	}

	private getArticleUrl(val: string): string {
		let componentName = '';
		let articleId = '';
		if (val) {
			const [component, id] = val.split('#').map((n) => n.replace(/https?:\/\//, ''));
			componentName = component.replace(/[\/.]/g, '-');
			articleId = id;
		}

		const { context = [] } = aps.navigation?.tree || {};
		const versionIndex = 2;
		const version = context.length
			? context[context.length - 1]?.version?.split('.').slice(0, versionIndex).join('.') || ''
			: '';
		const pathVersion = version ? `/${version}/` : '/';
		const articlePath = `${this.currentLanguage}/${componentName}${pathVersion}${articleId}`;

		this.setComponentName(componentName);
		this.setVersion(version);
		this.setArticleId(articleId);

		let formattedUrl = `${this.baseUrl}/404.html`;
		if (componentName && articleId) {
			formattedUrl = `${this.baseUrl}/${articlePath}.htm`;
		}

		return formattedUrl;
	}

	@Getter()
	public get currentLanguage(): string {
		const locale = aps.context._locale;
		const language = locale.split('_')[0];

		return LANGUAGE_MAPPING[language] || 'en-us';
	}

	@Getter()
	public get userId(): string {
		return `${aps.context.user?.aps.id}`;
	}

	@Getter()
	public get cleanArticlePath(): string {
		return this.articlePath.split('?')[0];
	}
}

const helpCenterServiceModule = new HelpCenterServiceModule();

export default helpCenterServiceModule;
